import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { trackerKeys } from './hooks';
import { prospectKeys } from './prospects';
import type { Prospect } from './prospects';
import type { ResearchRequest } from './researchQueue';
import type { SupabaseClient } from '../../lib/supabase';
import type { Tables } from '../../types/database.types';

/**
 * Batches: a working group of prospects, usually one month of one source.
 *
 * Kyu, 17 September: "I need them in batches, per month... so I can work on
 * them per month/batch at a time." The 364 LinkedIn connections span five
 * months and the month is how the chasing gets done — a batch is a week's
 * worth of work, not a label.
 *
 * Stored rather than derived from the prospect's date, because he also asked
 * to be able to move somebody between batches by hand. 20270151000100 has the
 * full reasoning. Nothing here knows about months: the import names batches
 * from the dates it reads, and Kyu names the ones he makes himself.
 */

export type ProspectBatch = Tables<'prospect_batches'>;

/** Where a batch's people came from. Groups the picker, nothing more. */
export type BatchSource = 'senku_daily' | 'linkedin_connections' | 'old_tracker' | 'manual';

/**
 * Group headings, in the order they appear in the picker: the two that fill
 * themselves first, then the migration, then whatever was made by hand.
 */
export const BATCH_SOURCES: { key: BatchSource; label: string }[] = [
  { key: 'senku_daily', label: "Senku's daily sheet" },
  { key: 'linkedin_connections', label: 'LinkedIn connections' },
  { key: 'old_tracker', label: 'Old tracker' },
  { key: 'manual', label: 'Made by hand' },
];

const SOURCE_LABELS = new Map(BATCH_SOURCES.map((entry) => [entry.key, entry.label]));

export function batchSourceLabel(source: string): string {
  return SOURCE_LABELS.get(source as BatchSource) ?? source;
}

export const batchKeys = {
  all: () => [...trackerKeys.all, 'prospect-batches'] as const,
};

function fail(message: string): never {
  throw new Error(message);
}

const BATCH_COLUMNS = 'id, name, source, created_by, created_at, updated_at';

export async function fetchProspectBatches(client: SupabaseClient): Promise<ProspectBatch[]> {
  const { data, error } = await client
    .from('prospect_batches')
    .select(BATCH_COLUMNS)
    .order('created_at', { ascending: false });
  if (error) fail(error.message);
  return Array.isArray(data) ? data : [];
}

export function useProspectBatches() {
  const { client } = useAuth();
  return useQuery({
    queryKey: batchKeys.all(),
    queryFn: () => fetchProspectBatches(client),
    retry: false,
  });
}

/**
 * "New batch…" from the picker, or from a row.
 *
 * Returns the batch so the caller can select it, or move the row into it,
 * without a second round trip.
 */
export function useCreateProspectBatch() {
  const { client, access } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = access.status === 'approved' ? access.profile.id : null;
  return useMutation({
    mutationFn: async (input: { name: string; source?: BatchSource }): Promise<ProspectBatch> => {
      const { data, error } = await client
        .from('prospect_batches')
        .insert({
          name: input.name.trim(),
          source: input.source ?? 'manual',
          created_by: createdBy,
        })
        .select(BATCH_COLUMNS)
        .single();
      if (error) fail(error.message);
      return data;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: batchKeys.all() });
    },
  });
}

/**
 * Move a prospect into a batch, or out of every batch.
 *
 * A plain update, not an RPC: which folder somebody sits in is not work done
 * on them, and logging it would put filing in the same timeline as the
 * messages actually sent. Status goes through set_prospect_status precisely
 * because status IS work; this is the other kind of change.
 */
export function useSetProspectBatch() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { prospectId: string; batchId: string | null }): Promise<void> => {
      const { error } = await client
        .from('prospects')
        .update({ batch_id: input.batchId })
        .eq('id', input.prospectId);
      if (error) fail(error.message);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: prospectKeys.all() });
    },
  });
}

/**
 * Find a batch by name, or make it. Names are unique case-folded in the
 * database (20270151000100), so a second import of a month the Hub already
 * has must land in the first one rather than fail or fork.
 *
 * Not an upsert: an upsert would quietly rewrite the source of a batch
 * somebody had renamed or refiled, and the existing batch is the one Kyu has
 * been working from.
 */
export async function findOrCreateBatch(
  client: SupabaseClient,
  input: { name: string; source: BatchSource; createdBy: string | null },
): Promise<ProspectBatch> {
  const name = input.name.trim();
  if (name === '') fail(batchErrorMessage('prospect_batches_named'));
  const read = () =>
    client
      .from('prospect_batches')
      .select(BATCH_COLUMNS)
      .ilike('name', escapeSearchTerm(name))
      .maybeSingle();
  const { data: found, error: readError } = await read();
  if (readError) fail(readError.message);
  if (found) return found;

  const { data, error } = await client
    .from('prospect_batches')
    .insert({ name, source: input.source, created_by: input.createdBy })
    .select(BATCH_COLUMNS)
    .single();
  if (error?.code === '23505') {
    const { data: raced, error: raceError } = await read();
    if (raceError) fail(raceError.message);
    if (raced) return raced;
    fail(batchErrorMessage('prospect_batches_name_unique'));
  }
  if (error) fail(error.message);
  return data;
}

/** Same literal ilike escape as files/queries.ts. */
function escapeSearchTerm(term: string): string {
  return term.replace(/[\\%_]/g, (character) => `\\${character}`);
}

export interface MoveProspectsBatchResult {
  moved: number;
  total: number;
  /** Empty on success; partial progress is retained on a failed chunk. */
  error: string;
}

export function useMoveProspectsBatch() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      prospectIds: string[];
      batchId: string | null;
    }): Promise<MoveProspectsBatchResult> => {
      const total = input.prospectIds.length;
      let moved = 0;
      for (let offset = 0; offset < total; offset += 100) {
        const { data, error } = await client
          .from('prospects')
          .update({ batch_id: input.batchId })
          .in('id', input.prospectIds.slice(offset, offset + 100))
          .select('id');
        if (error) return { moved, total, error: error.message };
        moved += Array.isArray(data) ? data.length : 0;
      }
      return { moved, total, error: '' };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: prospectKeys.all() });
      void queryClient.invalidateQueries({ queryKey: batchKeys.all() });
    },
  });
}

export function useRenameProspectBatch() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { batchId: string; name: string }): Promise<void> => {
      const name = input.name.trim();
      if (name === '') fail(batchErrorMessage('prospect_batches_named'));
      const { error } = await client
        .from('prospect_batches')
        .update({ name })
        .eq('id', input.batchId);
      if (error)
        fail(
          batchErrorMessage(
            error.code === '23505' ? 'prospect_batches_name_unique' : error.message,
          ),
        );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: batchKeys.all() });
    },
  });
}

export function batchErrorMessage(message: string): string {
  if (message.includes('prospect_batches_name_unique')) {
    return 'A batch with that name already exists.';
  }
  if (message.includes('prospect_batches_named')) {
    return 'Give the batch a name.';
  }
  if (message.includes('row-level security') || message.includes('not_a_sales_member')) {
    return 'You do not have access to prospect batches.';
  }
  return 'That change could not be saved. Try again.';
}

/** Which batch the list is showing. Null is "all prospects", the default. */
export type BatchFilter = string | null;

export function filterByBatch(prospects: readonly Prospect[], batchId: BatchFilter): Prospect[] {
  if (batchId === null) return [...prospects];
  if (batchId === UNBATCHED) return prospects.filter((p) => p.batch_id === null);
  return prospects.filter((p) => p.batch_id === batchId);
}

/**
 * The picker's own row for people in no batch at all. Not a batch id, and
 * deliberately not a real row in the table: "no batch" is the absence of a
 * fact, and giving it a row would mean every import had to decide whether to
 * point at it.
 */
export const UNBATCHED = 'unbatched';

export interface BatchGroup {
  /** A known source, or whatever raw value the database allowed. */
  source: string;
  label: string;
  batches: { batch: ProspectBatch; count: number }[];
}

/**
 * The picker, grouped by source, each group newest first, with counts.
 *
 * Batches with nobody in them still show: a batch made by hand a moment ago
 * is empty by definition, and hiding it would make "New batch…" look broken.
 */
export function groupBatches(
  batches: readonly ProspectBatch[],
  prospects: readonly Prospect[],
): BatchGroup[] {
  const counts = new Map<string, number>();
  for (const prospect of prospects) {
    if (prospect.batch_id === null) continue;
    counts.set(prospect.batch_id, (counts.get(prospect.batch_id) ?? 0) + 1);
  }
  const groups: BatchGroup[] = [];
  const seen = new Set<string>();
  for (const { key, label } of BATCH_SOURCES) {
    const mine = batches.filter((batch) => batch.source === key);
    mine.forEach((batch) => seen.add(batch.id));
    if (mine.length > 0) {
      groups.push({
        source: key,
        label,
        batches: mine.map((batch) => ({ batch, count: counts.get(batch.id) ?? 0 })),
      });
    }
  }
  // A source the database allows but this file has not been taught yet still
  // reaches the picker, under its own raw name, rather than vanishing.
  const rest = batches.filter((batch) => !seen.has(batch.id));
  for (const batch of rest) {
    const existing = groups.find((group) => group.source === batch.source);
    const entry = { batch, count: counts.get(batch.id) ?? 0 };
    if (existing) existing.batches.push(entry);
    else groups.push({ source: batch.source, label: batch.source, batches: [entry] });
  }
  return groups;
}

export function unbatchedCount(prospects: readonly Prospect[]): number {
  return prospects.filter((prospect) => prospect.batch_id === null).length;
}

/**
 * How many days back counts as "recently completed" for the picker shortcut.
 * Seven, Kyu's call (24 September): research that lands on a Friday is still
 * in the row the next week. Not tied to the research strip's two-day "stuck".
 */
export const RECENT_COMPLETION_DAYS = 7;

export interface RecentBatch {
  batch: ProspectBatch;
  count: number;
  completedAt: string;
}

/**
 * Batches with research finished in the last RECENT_COMPLETION_DAYS days,
 * newest first.
 *
 * Ticket hub-20260923-1790177314_056529: Hanneza cannot find a batch Senku
 * finished the day before without memorizing its exact name. Whether a batch
 * is "done" is not a stored fact — prospect_batches carries only id, name,
 * source, created_by, created_at, updated_at (database.types.ts, checked) —
 * so this reads it off the same research queue rows the research strip
 * already loads: the newest `completed_at` among the prospects filed under
 * each batch. A batch with nobody researched yet, or nobody researched
 * recently, does not appear here at all.
 */
export function recentlyCompletedBatches(
  batches: readonly ProspectBatch[],
  prospects: readonly Prospect[],
  research: readonly ResearchRequest[],
  now: Date,
  withinDays: number = RECENT_COMPLETION_DAYS,
): RecentBatch[] {
  const batchByProspect = new Map<string, string>();
  const countByBatch = new Map<string, number>();
  for (const prospect of prospects) {
    if (prospect.batch_id === null) continue;
    batchByProspect.set(prospect.id, prospect.batch_id);
    countByBatch.set(prospect.batch_id, (countByBatch.get(prospect.batch_id) ?? 0) + 1);
  }

  const newestCompletedByBatch = new Map<string, string>();
  for (const row of research) {
    if (row.state !== 'done' || row.completed_at === null) continue;
    const batchId = batchByProspect.get(row.prospect_id);
    if (batchId === undefined) continue;
    const seen = newestCompletedByBatch.get(batchId);
    if (seen === undefined || row.completed_at > seen) {
      newestCompletedByBatch.set(batchId, row.completed_at);
    }
  }

  const cutoff = now.getTime() - withinDays * 86_400_000;
  const result: RecentBatch[] = [];
  for (const batch of batches) {
    const completedAt = newestCompletedByBatch.get(batch.id);
    if (completedAt === undefined) continue;
    if (Date.parse(completedAt) < cutoff) continue;
    result.push({ batch, count: countByBatch.get(batch.id) ?? 0, completedAt });
  }
  // Newest completion first, so yesterday's batch is the top row.
  result.sort((a, b) => b.completedAt.localeCompare(a.completedAt));
  return result;
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * What the import calls the batch a row belongs in: source, then month.
 *
 * "LinkedIn connections · Feb 2026". Kyu works a month at a time, so the
 * month is the unit; the source is in the name as well as the grouping
 * because the name is what he reads in a Slack message or says out loud,
 * where there is no grouping to lean on.
 *
 * Built from the ISO date the parser already produced, by string rather than
 * through Date: `new Date('2026-03-01')` is midnight UTC, and in Manila
 * getMonth() on that is February. A batch of March connections filed under
 * February would be a quiet, plausible, wrong answer — the same class of bug
 * as the harness clock in drop 214, and the reason this takes the substring.
 */
export function batchNameFor(source: BatchSource, isoDay: string): string | null {
  const parts = /^(\d{4})-(\d{2})-\d{2}$/.exec(isoDay);
  if (parts === null) return null;
  const month = MONTH_NAMES[Number(parts[2]) - 1];
  if (month === undefined) return null;
  return `${batchSourceLabel(source)} · ${month} ${parts[1] ?? ''}`;
}

export interface PlannedBatch {
  name: string;
  source: BatchSource;
  /** The month, as `YYYY-MM`. Sorts; never shown. */
  month: string;
  /** Indexes into the row list the caller passed. */
  rows: number[];
}

/**
 * Which batches an import needs, and who goes in each.
 *
 * Rows with no date get no batch rather than a "no date" batch: a batch is
 * something you sit down and work, and "the ones we could not date" is not a
 * session's work. They land in the loose pile where they are visible.
 */
export function planBatches(
  rows: readonly { sourced_on: string | null }[],
  source: BatchSource,
): PlannedBatch[] {
  const byName = new Map<string, PlannedBatch>();
  rows.forEach((row, index) => {
    if (row.sourced_on === null) return;
    const name = batchNameFor(source, row.sourced_on);
    if (name === null) return;
    const existing = byName.get(name);
    if (existing) existing.rows.push(index);
    else byName.set(name, { name, source, month: row.sourced_on.slice(0, 7), rows: [index] });
  });
  // Newest month first, matching the picker. By month, NOT by name: the names
  // carry "Dec" and "Feb" and sorting those as text puts December 2025 between
  // August and February.
  return [...byName.values()].sort((a, b) => b.month.localeCompare(a.month));
}
